/**
 * Static UIKitML structure for the kit's panels. Authored as string constants
 * rather than files so a host needs no asset-copy step: `markupUrl()` wraps a
 * document in a Blob URL that IWSDK's runtime UIKitML loader fetches like any
 * other `PanelUI.config`.
 *
 * Dialect rules (IWSDK 0.5 / @drawcall/uikitml): no CSS comments inside
 * `<style>`, no `background-opacity` (alpha rides on `#rrggbbaa`), and every
 * dynamic-text element carries a literal placeholder child. State-dependent
 * styling (tile colours, visibility) is applied from TS via `setProperties`,
 * never expressed in markup.
 *
 * Element ids are the contract between this markup and the host binding.
 */

export const HAND_MENU_IDS = {
  root: 'hm-root',
  stepEyebrow: 'hm-step-eyebrow',
  stepTitle: 'hm-step-title',
  tile: (id: string) => `hm-tile-${id}`,
  tileGlyph: (id: string) => `hm-glyph-${id}`,
  tileLabel: (id: string) => `hm-label-${id}`,
} as const;

const HAND_MENU_TILES = ['home', 'tasks', 'redo', 'swap', 'help', 'mic', 'hear', 'clear', 'exit'] as const;

function handMenuTile(id: string): string {
  return (
    `<div id="hm-tile-${id}" class="hm-tile">` +
    `<span id="hm-glyph-${id}" class="hm-glyph">.</span>` +
    `<span id="hm-label-${id}" class="hm-label">.</span>` +
    `</div>`
  );
}

/** The vertical strip: readout, divider, tiles (all present; hidden per host), divider, EXIT. */
export const HAND_MENU_MARKUP = `<style>
  .hm-root { display: flex; flex-direction: column; align-items: center; padding-top: 12; padding-bottom: 12; padding-left: 10; padding-right: 10; gap: 8; border-radius: 20; border-width: 1; }
  .hm-readout { display: flex; flex-direction: column; align-items: center; width: 56; gap: 2; }
  .hm-eyebrow { font-size: 8; font-weight: bold; text-align: center; }
  .hm-title { font-size: 9; text-align: center; width: 56; }
  .hm-divider { width: 40; height: 1; }
  .hm-tile { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 56; height: 56; border-radius: 14; border-width: 1; gap: 2; cursor: pointer; }
  .hm-glyph { font-size: 18; text-align: center; }
  .hm-label { font-size: 10; font-weight: bold; text-align: center; }
</style>
<div id="hm-root" class="hm-root">
  <div class="hm-readout">
    <span id="hm-step-eyebrow" class="hm-eyebrow">STEP</span>
    <span id="hm-step-title" class="hm-title">.</span>
  </div>
  <div id="hm-divider-top" class="hm-divider"></div>
  ${HAND_MENU_TILES.filter((id) => id !== 'exit')
    .map(handMenuTile)
    .join('\n  ')}
  <div id="hm-divider-bottom" class="hm-divider"></div>
  ${handMenuTile('exit')}
</div>`;

export const STEP_FORM_IDS = {
  root: 'sf-root',
  panel: 'sf-panel',
  counter: 'sf-counter',
  brand: 'sf-brand',
  ticks: 'sf-ticks',
  tick: (i: number) => `sf-tick-${i}`,
  title: 'sf-title',
  description: 'sf-description',
  imagePlate: 'sf-image-plate',
  image: 'sf-image',
  actions: 'sf-actions',
  option: (i: number) => `sf-option-${i}`,
  optionLabel: (i: number) => `sf-option-label-${i}`,
  hint: 'sf-hint',
  grid: 'sf-grid',
  card: (i: number) => `sf-card-${i}`,
  cardVideo: (i: number) => `sf-card-video-${i}`,
  cardName: (i: number) => `sf-card-name-${i}`,
  cardCorner: (i: number) => `sf-card-corner-${i}`,
  cardMuted: (i: number) => `sf-card-muted-${i}`,
} as const;

const TICK_SLOTS = 12;
const OPTION_SLOTS = 4;
const CARD_SLOTS = 4;

/**
 * The step form: assistant grid (above), then the focus-card plate - eyebrow
 * row, progress ticks, title, description, image, actions, hint. Fixed slot
 * pools; the host shows/hides slots per step.
 */
export const STEP_FORM_MARKUP = `<style>
  .sf-root { display: flex; flex-direction: column; align-items: center; width: 520; gap: 18; }
  .sf-grid { display: flex; flex-direction: row; flex-wrap: wrap; justify-content: center; width: 488; gap: 12; }
  .sf-card { display: flex; flex-direction: column; justify-content: center; align-items: center; width: 238; height: 134; border-radius: 12; border-width: 1; background-color: #000000ff; }
  .sf-card-video { width: 100%; height: 100%; border-radius: 12; }
  .sf-card-name { font-size: 20; font-weight: bold; color: #ffffff; text-align: center; }
  .sf-card-corner { font-size: 14; font-weight: bold; color: #ffffff; }
  .sf-card-muted { width: 28; height: 28; border-radius: 14; align-items: center; justify-content: center; font-size: 18; font-weight: bold; color: #ffffff; }
  .sf-panel { display: flex; flex-direction: column; width: 520; padding-top: 24; padding-bottom: 24; padding-left: 26; padding-right: 26; gap: 12; border-radius: 18; border-width: 1; }
  .sf-top { display: flex; flex-direction: row; align-items: center; justify-content: space-between; width: 100%; }
  .sf-counter { font-size: 12; font-weight: bold; }
  .sf-brand { font-size: 11; font-weight: bold; }
  .sf-ticks { display: flex; flex-direction: row; width: 100%; height: 5; gap: 4; }
  .sf-tick { flex-grow: 1; height: 5; border-radius: 3; }
  .sf-title { font-size: 28; font-weight: bold; }
  .sf-description { font-size: 15; }
  .sf-image-plate { display: flex; width: 100%; height: 200; padding: 8; border-radius: 12; border-width: 1; background-color: #595e66f2; }
  .sf-image { width: 100%; height: 100%; }
  .sf-actions { display: flex; flex-direction: row; width: 100%; gap: 10; }
  .sf-option { display: flex; flex-grow: 1; height: 52; align-items: center; justify-content: center; border-radius: 14; border-width: 1; cursor: pointer; }
  .sf-option-label { font-size: 16; font-weight: bold; text-align: center; }
  .sf-hint { font-size: 11; }
</style>
<div id="sf-root" class="sf-root">
  <div id="sf-grid" class="sf-grid">
    ${Array.from({ length: CARD_SLOTS }, (_, i) =>
      `<div id="sf-card-${i}" class="sf-card">` +
      `<video id="sf-card-video-${i}" class="sf-card-video" src="" autoplay muted></video>` +
      `<span id="sf-card-name-${i}" class="sf-card-name">.</span>` +
      `<span id="sf-card-corner-${i}" class="sf-card-corner">.</span>` +
      `<div id="sf-card-muted-${i}" class="sf-card-muted"><span>Ø</span></div>` +
      `</div>`,
    ).join('\n    ')}
  </div>
  <div id="sf-panel" class="sf-panel">
    <div class="sf-top">
      <span id="sf-counter" class="sf-counter">.</span>
      <span id="sf-brand" class="sf-brand">.</span>
    </div>
    <div id="sf-ticks" class="sf-ticks">
      ${Array.from({ length: TICK_SLOTS }, (_, i) => `<div id="sf-tick-${i}" class="sf-tick"></div>`).join('')}
    </div>
    <span id="sf-title" class="sf-title">.</span>
    <span id="sf-description" class="sf-description">.</span>
    <div id="sf-image-plate" class="sf-image-plate"><img id="sf-image" class="sf-image" src="" /></div>
    <div id="sf-actions" class="sf-actions">
      ${Array.from({ length: OPTION_SLOTS }, (_, i) =>
        `<div id="sf-option-${i}" class="sf-option"><span id="sf-option-label-${i}" class="sf-option-label">.</span></div>`,
      ).join('\n      ')}
    </div>
    <span id="sf-hint" class="sf-hint">.</span>
  </div>
</div>`;

export const LABEL_PILL_IDS = { root: 'lp-root', text: 'lp-text' } as const;

/** The billboarded label pill of the location indicator. */
export const LABEL_PILL_MARKUP = `<style>
  .lp-root { display: flex; align-items: center; justify-content: center; width: 280; height: 56; padding: 8; border-radius: 16; border-width: 1; }
  .lp-text { font-size: 18; font-weight: bold; text-align: center; }
</style>
<div id="lp-root" class="lp-root"><span id="lp-text" class="lp-text">.</span></div>`;

/**
 * Wrap a UIKitML document in a Blob URL for `PanelUI.config`. Each call mints
 * a fresh URL (IWSDK caches documents per config string); revoke it via
 * `URL.revokeObjectURL` when the panel is disposed.
 */
export function markupUrl(markup: string): string {
  return URL.createObjectURL(new Blob([markup], { type: 'text/plain' }));
}

/** Every document this package ships, for build-time/parse validation. */
export const ALL_MARKUP: Record<string, string> = {
  handMenu: HAND_MENU_MARKUP,
  stepForm: STEP_FORM_MARKUP,
  labelPill: LABEL_PILL_MARKUP,
};
